import { useState } from 'react'
import { FileText, Plus, Eye, Save, Trash2, ChevronUp, ChevronDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useReportConfigs,
  useCreateReportConfig,
  useDeleteReportConfig,
  useGenerateReport,
  type ReportConfig,
} from '../hooks/useReportConfigs'
import { toast } from 'sonner'

interface ReportConfigDialogProps {
  siteId: string
  hasGsc?: boolean
  hasRankings?: boolean
  hasBacklinks?: boolean
  hasGa4?: boolean
}

export function ReportConfigDialog({
  siteId,
  hasGsc,
  hasRankings,
  hasBacklinks,
  hasGa4,
}: ReportConfigDialogProps) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'list' | 'edit'>('list')
  const [editingConfig, setEditingConfig] = useState<ReportConfig | null>(null)
  const [name, setName] = useState('')
  const [selectedSections, setSelectedSections] = useState<string[]>([])
  const [customText, setCustomText] = useState('')
  const [templateKey, setTemplateKey] = useState<string>('full_seo')

  const { data } = useReportConfigs(siteId)
  const createConfig = useCreateReportConfig(siteId)
  const deleteConfig = useDeleteReportConfig(siteId)
  const generateReport = useGenerateReport(siteId)

  const templates = data?.templates ?? {}
  const availableSections = data?.availableSections ?? []
  const configs = data?.configs ?? []

  // Track which sections are available based on connected integrations
  const sectionAvailability: Record<string, boolean> = {
    gsc_performance: !!hasGsc,
    rankings: !!hasRankings,
    backlinks: !!hasBacklinks,
    ga4_traffic: !!hasGa4,
  }

  const startNewConfig = () => {
    const tmpl = templates[templateKey]
    setName(tmpl?.name ?? 'Custom Report')
    setSelectedSections(tmpl?.sections ?? [])
    setCustomText('')
    setEditingConfig(null)
    setMode('edit')
  }

  const startEditConfig = (config: ReportConfig) => {
    setName(config.name)
    setSelectedSections(config.sections)
    setCustomText(config.custom_text ?? '')
    setEditingConfig(config)
    setMode('edit')
  }

  const toggleSection = (key: string) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    )
  }

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newSections = [...selectedSections]
    const target = direction === 'up' ? index - 1 : index + 1
    if (target < 0 || target >= newSections.length) return
    ;[newSections[index], newSections[target]] = [newSections[target]!, newSections[index]!]
    setSelectedSections(newSections)
  }

  const handleSave = async () => {
    if (!name.trim() || selectedSections.length === 0) {
      toast.error('Name and at least one section required')
      return
    }
    try {
      await createConfig.mutateAsync({
        name,
        sections: selectedSections,
        customText: customText || undefined,
      })
      toast.success('Report config saved')
      setMode('list')
    } catch {
      toast.error('Failed to save config')
    }
  }

  const handlePreview = () => {
    generateReport.mutate(
      { sections: selectedSections },
      {
        onSuccess: (blobUrl) => {
          window.open(blobUrl, '_blank')
        },
        onError: () => toast.error('Failed to generate preview'),
      },
    )
  }

  const handleGenerateFromConfig = (config: ReportConfig) => {
    generateReport.mutate(
      { configId: config.id },
      {
        onSuccess: (blobUrl) => {
          window.open(blobUrl, '_blank')
        },
        onError: () => toast.error('Failed to generate report'),
      },
    )
  }

  const handleGenerateFromTemplate = (key: string) => {
    generateReport.mutate(
      { template: key },
      {
        onSuccess: (blobUrl) => {
          window.open(blobUrl, '_blank')
        },
        onError: () => toast.error('Failed to generate report'),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FileText className="h-4 w-4 mr-1.5" />
          Report Builder
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'list' ? 'Report Builder' : editingConfig ? 'Edit Config' : 'New Report Config'}</DialogTitle>
          <DialogDescription>
            {mode === 'list'
              ? 'Choose a template or create a custom report configuration.'
              : 'Select which sections to include and their order.'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'list' ? (
          <div className="space-y-4">
            {/* Templates */}
            <div>
              <Label className="text-sm font-medium">Quick Generate</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(templates).map(([key, tmpl]) => (
                  <button
                    key={key}
                    onClick={() => handleGenerateFromTemplate(key)}
                    disabled={generateReport.isPending}
                    className="text-left p-3 rounded-lg border hover:border-primary/50 hover:bg-muted/50 transition-colors"
                  >
                    <div className="font-medium text-sm">{tmpl.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{tmpl.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Saved configs */}
            {configs.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Saved Configs</Label>
                <div className="space-y-2 mt-2">
                  {configs.map(config => (
                    <div key={config.id} className="flex items-center gap-2 p-2 rounded-md border">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{config.name}</div>
                        <div className="text-xs text-muted-foreground">{config.sections.length} sections</div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => handleGenerateFromConfig(config)} disabled={generateReport.isPending}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => startEditConfig(config)}>
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive" onClick={() => deleteConfig.mutate(config.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create new */}
            <div className="flex items-center gap-2">
              <Select value={templateKey} onValueChange={setTemplateKey}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(templates).map(([key, tmpl]) => (
                    <SelectItem key={key} value={key}>{tmpl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={startNewConfig}>
                <Plus className="h-4 w-4 mr-1.5" />
                Customise
              </Button>
            </div>

            {generateReport.isPending && (
              <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Generating report...
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="config-name">Config Name</Label>
              <Input
                id="config-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Monthly Client Report"
              />
            </div>

            <div>
              <Label>Sections (drag to reorder)</Label>
              <div className="space-y-1 mt-2">
                {availableSections.map(section => {
                  const isSelected = selectedSections.includes(section.key)
                  const selectedIndex = selectedSections.indexOf(section.key)
                  const isUnavailable = section.key in sectionAvailability && !sectionAvailability[section.key]

                  return (
                    <div key={section.key} className={`flex items-center gap-2 p-2 rounded border ${isSelected ? 'border-primary/30 bg-primary/5' : 'border-transparent'} ${isUnavailable ? 'opacity-50' : ''}`}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSection(section.key)}
                        disabled={isUnavailable}
                      />
                      <span className="flex-1 text-sm">{section.label}</span>
                      {isUnavailable && (
                        <span className="text-xs text-muted-foreground">Not connected</span>
                      )}
                      {isSelected && (
                        <div className="flex gap-0.5">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSection(selectedIndex, 'up')} disabled={selectedIndex === 0}>
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSection(selectedIndex, 'down')} disabled={selectedIndex === selectedSections.length - 1}>
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="custom-text">Custom Notes (optional)</Label>
              <Textarea
                id="custom-text"
                value={customText}
                onChange={e => setCustomText(e.target.value)}
                placeholder="Add notes or context for the report reader..."
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode('list')} className="flex-1">
                Back
              </Button>
              <Button variant="outline" onClick={handlePreview} disabled={generateReport.isPending || selectedSections.length === 0}>
                {generateReport.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
                Preview
              </Button>
              <Button onClick={handleSave} disabled={createConfig.isPending || selectedSections.length === 0} className="flex-1">
                {createConfig.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
